import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Header } from '../../components';
import { UsageEventsTracker } from '../../services/UsageEventsTracker';
import { DataHealthApi } from '../../services/api/DataHealthApi';
import { UIPreferencesApi } from '../../services/api/UIPreferencesApi';
import { ONBOARDING_V2_ENABLED } from '../../config/featureFlags';
import { shouldShowOnboarding } from '../../utils/onboardingEligibility';
import AccountRoleSelector from '../../parent/components/AccountRoleSelector';
import '../../parent/components/AccountRoleSelector.css';
import { ParentApi } from '../../services/api/ParentApi';
import './LoginPage.css';

function LoginPage({ hideFooter = false }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(localStorage.getItem('remember_me_flag') === 'true');
  const [selectedRole, setSelectedRole] = useState('student');
  
  const { login, accessToken, loadUser } = useAuth();
  const navigate = useNavigate();

  // Reset scroll position to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Redirect already-authenticated students to dashboard (parent portal is separate)
  useEffect(() => {
    if (accessToken) {
      navigate('/dashboard', { replace: true });
    }
  }, [accessToken, navigate]);

  // Wake up Data-Service on page load (in case it's sleeping in Cloud Run)
  useEffect(() => {
    const wakeUpDataService = async () => {
      try {
        console.log('LoginPage: Waking up Data-Service...');
        await DataHealthApi.checkHealth();
        console.log('LoginPage: Data-Service is awake');
      } catch (error) {
        console.warn('LoginPage: Failed to wake up Data-Service:', error);
        // Don't block login if health check fails - service might still be available
      }
    };

    wakeUpDataService();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    setLoading(true);

    // Track login attempt
    try {
      UsageEventsTracker.recordUserAction('LOGIN_ATTEMPT', {
        action: 'click',
        category: 'auth',
        entityType: 'button',
        label: 'Login Button',
        metadata: { email, rememberMe }
      });
    } catch (trackError) {
      console.warn('LoginPage: Failed to track login attempt', trackError);
    }

    try {
      console.log('LoginPage: Attempting login...');
      const result = await login(email, password, rememberMe);
      
      if (result.success) {
        console.log('LoginPage: Login successful, redirecting...');
        // Parent role: enable the parent role (idempotent) and enter the portal.
        if (selectedRole === 'parent') {
          try { await ParentApi.becomeParent(); } catch (e) { console.warn('becomeParent failed', e); }
          await loadUser?.();
          navigate('/parent/overview');
          return;
        }
        // Check if user needs onboarding (new user)
        if (ONBOARDING_V2_ENABLED) {
          // Fetch preferences fresh from the API — the PreferencesContext hasn't
          // updated yet at this point so we can't rely on the stale context value.
          let freshPrefs = {};
          try {
            const resultUser = result.user || {};
            const uid = resultUser.userId || resultUser.id;
            if (uid) {
              const rawDto = await UIPreferencesApi.get(uid);
              freshPrefs = rawDto?.preferences || rawDto || {};
            }
          } catch (e) {
            // 404 = user has no preferences record yet (new user) — keep empty
            console.warn('LoginPage: Could not fetch preferences:', e?.status || e?.message);
          }
          const needsOnboarding = shouldShowOnboarding({ user: result.user || {}, uiPreferences: freshPrefs });
          if (needsOnboarding) {
            // Org admins / course admins still see onboarding but the Welcome
            // step will show a role-appropriate tile. Let them through.
            navigate('/onboarding');
            return;
          }
        }
        // Route to the portal matching the selected role
        if (selectedRole === 'teacher') {
          navigate('/teacher/dashboard');
        } else {
          navigate('/dashboard');
        }
      } else {
        console.error('LoginPage: Login failed:', result.message);
        setErrorMessage(result.message || 'Login failed. Please try again.');
      }
    } catch (error) {
      console.error('LoginPage: Login error:', error);
      setErrorMessage(error.message || 'An error occurred during login. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="login-page" style={{ backgroundImage: "url('/landing_page_hero/docuhoundsbackgroundclassroom.png')" }}>
      <Header />
      <div className="login-bg-overlay" aria-hidden="true"></div>
      
      <div className="login-card">
        {/* Watermark */}
        <div className="login-card-watermark" aria-hidden="true">
          <img src="/scout_images/scout_thumbs_up.png" alt="" />
        </div>
        {/* Left side: Login Form */}
        <div className="login-form-side">
          <h2>Login to DocuHounds</h2>
          <p className="login-tagline">Your AI-powered study HQ, guarded by Scout.</p>
          
          <AccountRoleSelector selectedRole={selectedRole} onRoleChange={setSelectedRole} />

          {errorMessage && <div className="error-message">{errorMessage}</div>}
          
          <form onSubmit={handleSubmit}>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <div className="remember-me-row">
              <label htmlFor="rememberMe" className="remember-label">
                <input
                  id="rememberMe"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span>Remember me</span>
              </label>
            </div>
            <button 
              type="submit" 
              className="login-button"
              disabled={loading || !email || !password}
            >
              {loading ? (
                <>
                  <div className="button-spinner"></div>
                  Scout is fetching your workspace…
                </>
              ) : (
                <>
                  <span className="button-icon">🐾</span>
                  Sign In
                </>
              )}
            </button>
          </form>

          {!hideFooter && (
            <div className="login-footer">
              <p>
                Don't have an account? <Link to="/register">Sign up here</Link>
              </p>
              <p>
                <Link to="/forgot-password">Forgot your password?</Link>
              </p>
              <p className="login-footer-tagline">
                Built for students. Powered by AI.
              </p>
            </div>
          )}
        </div>

        {/* Right side: Scout Hero Panel */}
        <div className="scout-hero-panel">
          <div className="scout-hero-content">
            <div className="scout-avatar-wrapper">
              <img 
                src="/scout_images/scout_thumbs_up.png" 
                alt="Scout giving a thumbs up" 
                className="scout-avatar"
              />
            </div>
            <h3 className="scout-welcome">Pick up where you left off.</h3>
            <p className="scout-message">
              Your courses, docs, deadlines, and study tools are all here. Log in and get back to work without wasting time.
            </p>
            <div className="scout-bullets">
              <div className="scout-bullet"><span className="scout-bullet-icon">✓</span><span>Everything in one place</span></div>
              <div className="scout-bullet"><span className="scout-bullet-icon">✓</span><span>Pick up fast</span></div>
              <div className="scout-bullet"><span className="scout-bullet-icon">✓</span><span>Stay on track</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
